import { Box, Divider, IconButton, TextField, useTheme } from "@material-ui/core";
import { ClearRounded as ClearIcon, Search as SearchIcon } from "@material-ui/icons";
import { DocumentNode } from "graphql";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMapThemeClasses } from "../../hooks/use-map-theme-styles";
import { Column } from "../../types/tableTypes";
import { FiltersRoot } from "../filters/FiltersRoot";
import { FilterListStateType, FilterSelectionType } from "../filters/models";
import LyfeColumnsCard, { ConfigurableTableType } from "./LyfeColumnsCard";
import { TableTypes } from "./LyfeDataTable";
import { InternalRefetchQueriesInclude } from "@apollo/client";

interface ILyfeToolbar {
	title: TableTypes;
	totalRecords?: number;
	filterInitialState?: FilterListStateType;
	filterPersistedState?: object;
	saveFilterQuery?: DocumentNode;
	countQuery?: DocumentNode;
	module?: string;
	filteredRecords: number;
	children?: React.ReactNode;
	handleSearch?: (input: string) => void;
	buttons: Array<JSX.Element>;
	showButtonsNextToColumns?: boolean;
	viewButtons?: Array<JSX.Element>;
	infoIcons?: Array<JSX.Element>;
	loading?: boolean;
	filterExtensionKey?: string;
	tableColumns?: Array<Column<any>>;
	configurableTableType?: ConfigurableTableType;
	refetchQuery?: InternalRefetchQueriesInclude;
}

const getNonEmptyFilterOptions = (filters: FilterListStateType): FilterListStateType => {
	return filters.map((initialFilter) => {
		if (initialFilter.selectionType === FilterSelectionType.LIST) {
			return {
				...initialFilter,
				options: initialFilter.isBooleanList
					? initialFilter.options
					: initialFilter.options.filter((option) => Boolean(option.value)),
			};
		}
		return { ...initialFilter };
	});
};

function LyfeToolbar(props: ILyfeToolbar) {
	const [searchInput, setSearchInput] = useState("");
	const { search } = useMapThemeClasses();
	const { t } = useTranslation();
	const { shadows } = useTheme();

	useEffect(() => {
		if (props.loading === false) {
			clearInput();
		}
	}, [props.loading]);

	function handleSearchInput(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
		setSearchInput(e.target.value);
		props.handleSearch!(e.target.value);
	}

	function clearInput() {
		setSearchInput("");
		props.handleSearch!("");
	}

	const buttonComponent = (
		<Box display="flex" alignItems="center" justifyContent="space-between" flexShrink={0}>
			{props.buttons.map((button, i) => (
				<Box key={i} ml={i > 0 ? 1 : 0}>
					{button}
				</Box>
			))}
			&nbsp;
		</Box>
	);

	return (
		<>
			<Box
				width="100%"
				display="flex"
				alignItems="center"
				p={3}
				style={{
					boxShadow: shadows[3],
					width: "100%",
					position: "sticky",
					top: 100,
					backgroundColor: "white",
					zIndex: 4,
					borderRadius: "4px",
				}}
			>
				<Box display="flex" alignItems="center" flexGrow={1}>
					{props.handleSearch && (
						<Box flexShrink={0}>
							<TextField
								variant="outlined"
								InputProps={{
									classes: { root: search },
									startAdornment: <SearchIcon color="primary" />,
									endAdornment: searchInput ? (
										<IconButton onClick={clearInput}>
											<ClearIcon />
										</IconButton>
									) : (
										""
									),
								}}
								placeholder={` ${t("search")} ${t(props.title)}...`}
								value={searchInput}
								onChange={handleSearchInput}
							/>
						</Box>
					)}
					<Box display="flex" alignItems="center" flexGrow={1}>
						{props.filterInitialState && props.saveFilterQuery && props.countQuery && (
							<FiltersRoot
								title={props.title}
								initialState={getNonEmptyFilterOptions(props.filterInitialState)}
								saveFilterQuery={props.saveFilterQuery}
								countQuery={props.countQuery}
								module={props.module}
								filterPersistedState={props.filterPersistedState}
								refetchQueries={props.refetchQuery}
							/>
						)}
					</Box>
				</Box>
				{!props.showButtonsNextToColumns && buttonComponent}
			</Box>
			{((props.viewButtons && props.viewButtons.length > 0) ||
				(props.tableColumns && props.tableColumns.length > 0) ||
				(props.infoIcons && props.infoIcons.length > 0)) && (
				<Box
					width="100%"
					style={{
						boxShadow: shadows[3],
						width: "100%",
						position: "sticky",
						top: 100,
						backgroundColor: "white",
						zIndex: 3,
					}}
				>
					<Divider style={{ flexGrow: 1, backgroundColor: "#cddcdc" }} />
					<Box
						width="100%"
						px={3}
						py={1.25}
						display="flex"
						alignItems="center"
						justifyContent={props.viewButtons && props.viewButtons.length > 0 ? "space-between" : "end"}
					>
						{props.viewButtons && props.viewButtons.length > 0 && (
							<Box display="flex" width="100%">
								{props.viewButtons?.map((button, i) => (
									<Box key={i} ml={i > 0 ? 1 : 0} width="100%">
										{button}
									</Box>
								))}
							</Box>
						)}
						<Box display="flex" alignItems="center">
							{props.tableColumns && props.tableColumns.length > 0 && (
								<Box display="flex">
									<LyfeColumnsCard
										title={t(props.title)}
										columns={props.tableColumns}
										configurableTableType={props.configurableTableType!}
									/>
								</Box>
							)}
							{props.showButtonsNextToColumns && buttonComponent}
							{props.infoIcons && props.infoIcons.length > 0 && (
								<Box display="flex">
									{props.infoIcons?.map((icon, i) => (
										<Box key={i} ml={i > 0 ? 1 : 0}>
											{icon}
										</Box>
									))}
								</Box>
							)}
						</Box>
					</Box>
				</Box>
			)}
			<div
				style={{
					backgroundColor: "rgba(251,248,245,1)",
					height: 24,
					position: "sticky",
					top: 188,
					zIndex: 1,
					margin: "0px -5px",
					width: "101%",
				}}
			></div>
		</>
	);
}

export default memo(LyfeToolbar);
